import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../lib/translations';
import Section from '../components/UI/Section';
import { motion } from 'framer-motion';

const About = () => {
  const { lang } = useLanguage();
  const t = translations[lang];

  const content = {
    da: {
      title: "Min baggrund",
      subtitle: "Kay Elizabeth Worsfold",
      intro: "Jeg er autoriseret klinisk psykolog (godkendt af Styrelsen for Patientsikkerhed) med mere end 20 års erfaring med at tilbyde psykoterapi både på dansk og engelsk.",
      education: [
        "Jeg er uddannet psykolog (cand.psych.) fra Københavns Universitet og har desuden en ph.d.-grad i klinisk psykologi fra Københavns Universitet på baggrund af min forskning i Mindfulness-baseret kognitiv terapi. Jeg er godkendt specialist og supervisor i klinisk psykologi/psykoterapi fra Dansk Psykolog Forening.",
        "Jeg har en omfattende klinisk efteruddannelse inden for Compassionfokuseret Terapi (CFT) og Mindfulness-baseret kognitiv terapi (MBKT). Senest i 2026 gennemførte jeg en 1-årig intensiv postgraduate diplomuddannelse i CFT fra Compassionate Mind Foundation, UK.",
        "Inden jeg etablerede egen fuldtidspraksis, har jeg mange års erfaring fra den ambulante psykiatri (affektive lidelser samt angst og OCD) og i behandling af arbejdsrelateret stress under tidligere ansættelse på Stressreduktionsklinikken på Bispebjerg Hospital."
      ],
      experience: "",
      quote: "Det er vigtigt for mig at tilbyde en psykologisk behandling præget af respekt, høj faglighed, nærvær og omsorg.",
      membershipsTitle: "Medlemskaber & Registreringer",
      memberships: [
        {
          name: "Medlem af Dansk Psykolog Forening",
          logo: "https://assets.auxodev.dk/assets/d2a473f1-f23b-48f1-982c-7e943d864f39/image_2.png",
          link: "https://www.dp.dk/"
        },
        {
          name: "Medlem af Mindfulness Foreningen",
          logo: "https://assets.auxodev.dk/assets/d2a473f1-f23b-48f1-982c-7e943d864f39/deabdddf-d4e2-4cf0-998a-61ead6ea5996.png",
          link: "https://mindfulnessforeningen.dk/"
        },
        {
          name: "Registreret med det internationale ACCESS MBCT-register",
          logo: "https://assets.auxodev.dk/assets/d2a473f1-f23b-48f1-982c-7e943d864f39/image_3.png",
          link: "https://www.accessmbct.com/"
        }
      ]
    },
    en: {
      title: "About Me",
      subtitle: "Kay Elizabeth Worsfold",
      intro: "I am a licensed Clinical Psychologist with more than 20 years of experience providing coaching and therapy in English and Danish.",
      education: [
        "Originally from London and educated in the UK, I worked as an analyst and managerial associate in the financial sector, before moving to Copenhagen to continue my career at the age of 28. After switching career paths, I completed my Master’s and PhD in Psychology at the University of Copenhagen.",
        "I am authorized by the Danish Patient Safety Authority (Styrelsen for Patientsikkerhed) and an approved specialist in Clinical Psychology and Supervision with the Danish Psychological Association.",
        "My main clinical specialism is in Compassion Focused Therapy (CFT) and Mindfulness-based Cognitive Therapy (MBCT).",
        "I hold a British Psychological Society Approved Post Graduate Diploma in CFT from the Compassionate Mind Foundation, UK.",
        "I have completed extensive clinical training in Mindfulness-based Cognitive Therapy (MBCT) and Mindfulness-based Stress Reduction, including a one-year MBCT teacher trainer program with Oxford Mindfulness, UK.  I am an approved MBCT Therapist with the International listing, Access MBCT, and hold a PhD in Mindfulness-based Cognitive Therapy for Depression.",
        "Prior to establishing a private practice, I worked for many years in the Danish out-patient Health Services both in the area of affective disorders (including depression) and anxiety and OCD. I have also worked at the Stress Reduction Clinic, Bispebjerg Hospital, where I specialized in work-related stress.",
        "Previous External Lecturer in Positive Psychology for the Study Abroad Institution, DIS."
      ],
      experience: "",
      quote: "It is important to me to offer a warm, respectful, safe and confidential space for our work.",
      membershipsTitle: "Memberships & Registrations",
      memberships: [
        {
          name: "Member of the Danish Psychological Association",
          logo: "https://assets.auxodev.dk/assets/d2a473f1-f23b-48f1-982c-7e943d864f39/image_2.png",
          link: "https://www.dp.dk/"
        },
        {
          name: "Member of the Mindfulness Association",
          logo: "https://assets.auxodev.dk/assets/d2a473f1-f23b-48f1-982c-7e943d864f39/deabdddf-d4e2-4cf0-998a-61ead6ea5996.png",
          link: "https://mindfulnessforeningen.dk/"
        },
        {
          name: "Registered with the international ACCESS MBCT register",
          logo: "https://assets.auxodev.dk/assets/d2a473f1-f23b-48f1-982c-7e943d864f39/image_3.png",
          link: "https://www.accessmbct.com/"
        }
      ]
    }
  };

  const c = content[lang];

  return (
    <div className="pt-20">
      <section className="bg-stone-50 py-12 md:py-16 lg:py-32">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-4xl md:text-5xl font-serif text-stone-900 mb-8">
                {c.title}
              </h1>
              <p className="text-xl text-stone-700 leading-relaxed font-light mb-8">
                {c.intro}
              </p>
              <div className="space-y-6 text-stone-600 leading-relaxed">
                {c.education.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
                {c.experience && <p className="font-medium text-stone-800">{c.experience}</p>}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              className="relative flex justify-center lg:justify-start w-full"
            >
              <div className="w-full max-w-[380px] relative">
                <img 
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/d2a473f1-f23b-48f1-982c-7e943d864f39/image_13.png" 
                  alt="Kay Elizabeth Worsfold" 
                  className="w-full h-auto object-contain rounded-2xl"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
